import { ShadowlessLitElement } from "@/components/meta/shadowless_lit_element";
import { html, svg, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
import { isWebKit } from "@/utilities";
import { SeriesKind } from "@/series";

export type SeriesProgressStatus = "not-yet-begun" | "started" | "completed" | "wrong";
export type SeriesDeadlineStatus = "met" | "missed" | null;
export type SeasonTheme = "christmas" | "december" | "valentine" | "mario-day" | "pi-day" | null;

/**
 * @element d-series-icon
 *
 * this icon visualizes the status of a series for a give user
 *
 * @cssprop --icon-color - the color of the icon
 * @cssprop --icon-background-color - the background color of the icon
 * @cssprop --deadline-icon-color - the color of the deadline icon
 * @cssprop --deadline-icon-background-color - the background color of the deadline icon
 *
 * @prop {string} deadline - The status of the deadline, if any
 * @prop {string} season - The season theme to apply, if any
 * @prop {string} progress - The progress of the user in the series
 * @prop {number} size - The size of the icon in pixels
 * @prop {string} status - The status of the series, displayed as a tooltip
 * @prop {string} type - The type of the series
 */
@customElement("d-series-icon")
export class SeriesIcon extends ShadowlessLitElement {
    clearInitialContent = new ClearInitialContentController(this);

    @property({ type: String })
    kind: SeriesKind = SeriesKind.REGULAR;
    @property({ type: String })
    progress: SeriesProgressStatus = "not-yet-begun";
    @property({ type: String })
    deadline: SeriesDeadlineStatus = null;
    @property({ type: String })
    season: SeasonTheme = null;
    @property({ type: Number })
    size = 40;
    @property({ type: String })
    status = "";

    static PROGRESS_ICONS: Record<SeriesProgressStatus, string> = {
        "not-yet-begun": "mdi-school",
        started: "mdi-thumb-up",
        completed: "mdi-check-bold",
        wrong: "mdi-close",
    };

    static DEADLINE_ICONS: Record<SeriesDeadlineStatus, string> = {
        met: "mdi-alarm-check",
        missed: "mdi-alarm-off",
    };

    get progressIcon(): string {
        return SeriesIcon.PROGRESS_ICONS[this.progress];
    }

    get deadlineIcon(): string {
        return SeriesIcon.DEADLINE_ICONS[this.deadline];
    }

    get deadlineClass(): string {
        return this.deadline ? `deadline-${this.deadline}` : "";
    }

    protected render(): TemplateResult {
        switch (this.kind) {
            case SeriesKind.EXAM:
                return this.renderExamSeriesIcon();
            case SeriesKind.REGULAR:
                return this.renderRegularSeriesIcon();
            case SeriesKind.OPTIONAL:
                return this.renderOptionalSeriesIcon();
        }
    }

    private renderBaseSvg({
        iconClass,
        iconColor = "var(--icon-color)",
        deadlineIcon = null,
        gradient = null,
        overlay = null,
        svgClass = null,
        title = "",
    }: {
        iconClass: string;
        iconColor?: string;
        deadlineIcon?: string | null;
        gradient?: TemplateResult | null;
        overlay?: TemplateResult | null;
        svgClass?: string;
        title?: string;
    }): TemplateResult {
        return html`
            <svg
                viewBox="0 0 40 40"
                style="width: ${this.size}px; height: ${this.size}px;"
                class="series-icon ${svgClass}"
                title="${title}"
                aria-label="${title}"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
            >
                ${gradient ? gradient : ""}
                <g class="icon-base">
                    <circle cx="50%" cy="50%" r="50%" fill="${iconColor}" class="outer-circle"></circle>
                    <circle cx="50%" cy="50%" r="42%" fill="var(--icon-background-color)"></circle>
                    <foreignObject x="8" y="8" height="24" width="24" style="color: ${iconColor}">
                        <i class="mdi ${iconClass}" aria-hidden="true"></i>
                    </foreignObject>
                    ${
                        deadlineIcon
                            ? svg`
                            <circle cx="33" cy="34.5" r="11" fill="var(--deadline-icon-background-color)"></circle>
                            <foreignObject x="24" y="26" height="18" width="18" style="color: var(--deadline-icon-color)">
                                <i class="mdi mdi-18 ${deadlineIcon}" aria-hidden="true"></i>
                            </foreignObject>
                        `
                            : ""
                    }
                </g>
                ${overlay ? overlay : ""}
            </svg>
        `;
    }

    private renderExamSeriesIcon(): TemplateResult {
        return this.renderBaseSvg({
            iconColor: "var(--exam-series-icon-color)",
            iconClass: "mdi-format-list-checks",
            title: this.status,
        });
    }

    private renderOptionalSeriesIcon(): TemplateResult {
        return this.renderBaseSvg({
            iconColor: "var(--optional-series-icon-color)",
            iconClass: "mdi-star",
            title: this.status,
        });
    }

    private renderRegularSeriesIcon(): TemplateResult {
        const gradient =
            this.progress === "completed" && this.deadline !== "missed"
                ? svg`
                <defs>
                    <linearGradient id="rainbow" gradientTransform="rotate(135, 0.5, 0.5)" >
                        <stop stop-color="var(--red)" offset="1%" />
                        <stop stop-color="var(--orange)" offset="25%" />
                        <stop stop-color="var(--yellow)" offset="40%" />
                        <stop stop-color="var(--green)" offset="60%" />
                        <stop stop-color="var(--blue)" offset="75%" />
                        <stop stop-color="var(--purple)" offset="99%" />
                    </linearGradient>
                </defs>
            `
                : null;

        const overlay =
            // On WebKit, we only render seasonal overlays for default size series
            // statuses to not run into webkit bugs: https://bugs.webkit.org/show_bug.cgi?id=23113
            this.season && (!isWebKit() || this.size === 40)
                ? svg`
                  <foreignObject width="100%" height="100%" class="overlay">
                      <div></div>
                  </foreignObject>
            `
                : null;

        return this.renderBaseSvg({
            iconColor: "var(--icon-color)",
            iconClass: this.progressIcon,
            deadlineIcon: this.deadlineIcon,
            gradient,
            overlay,
            svgClass: `${this.season} ${this.progress} ${this.deadlineClass}`,
            title: this.status,
        });
    }
}
